import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import API from "../constants/API";
import { getTeamID, JSONParser } from "../comman/function";
import { authHeader } from "../comman/mainfunction";
import { GET_FEATURE_FLAGS_URL } from "../comman/url";

export interface FeatureFlags {
  ebsSyncEnabled: boolean;
  ebsOrgId: number | null;
  supplierInviteDisabled: boolean;
  manualSupplierCreationDisabled: boolean;
  emailImportEnabled: boolean;
  physicalScannerEnabled: boolean;
  enableWorkflowRouting: boolean;
  supplierPortalEnabled: boolean;
  signupEnabled: boolean;
  mobileBlockerEnabled: boolean;
  approvalLimitsEnabled: boolean;
  ssoEnabled: boolean;
}

const FeatureFlagContext = createContext<FeatureFlags | undefined>(undefined);

// Default feature flags (used as fallback if API fails)
// Defaults are set for enterprise customers (like WCPSS) - tenants can override via tblteam.feature_flags
const DEFAULT_FLAGS = {
  ebsSyncEnabled: true, // EBS supplier sync enabled
  ebsOrgId: null, // Customer-specific, set per tenant
  supplierInviteDisabled: true, // Disable supplier self-invite (enterprise default)
  manualSupplierCreationDisabled: true, // Suppliers come from EBS, not manual creation
  emailImportEnabled: false, // Email import disabled by default
  physicalScannerEnabled: false, // Physical scanner disabled by default
  enableWorkflowRouting: false, // Workflow routing disabled by default
  supplierPortalEnabled: false, // Supplier chat/portal disabled by default
  signupEnabled: false, // Self-signup disabled by default
  mobileBlockerEnabled: true, // Block mobile phones (tablets allowed)
  approvalLimitsEnabled: false, // Approval Limits tab on Users page (disabled for WCPSS)
  ssoEnabled: false, // SSO configuration visibility in Settings
} as const satisfies FeatureFlags;

interface FeatureFlagProviderProps {
  children: ReactNode;
}

/**
 * Feature Flag Provider
 * Fetches feature flags from API on mount and provides them to children
 * Prevents flash by not rendering children until flags are loaded
 */
export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        const teamId = getTeamID();
        if (!teamId) {
          console.warn("[FeatureFlags] No teamId found, using defaults");
          setIsLoading(false);
          return;
        }

        const config = {
          method: "GET",
          url: GET_FEATURE_FLAGS_URL,
          headers: authHeader() as unknown as Record<string, string>,
          params: { teamId },
        };

        const response = await API(config);
        const decrypted = response.data;
        const parsed = JSONParser<{ status: string | null; data: Partial<FeatureFlags> | null }>(
          decrypted,
          { status: null, data: null }
        );

        if (parsed.status === "Success" && parsed.data) {
          setFlags({
            ...DEFAULT_FLAGS,
            ...parsed.data,
          });
        } else {
          console.warn("[FeatureFlags] API returned non-success, using defaults");
        }
      } catch (error) {
        console.error("[FeatureFlags] Error fetching feature flags:", error);
        // Keep default flags on error
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeatureFlags();
  }, []);

  // Don't render children until flags are loaded
  // This prevents the "flash" of default UI before feature flags apply
  if (isLoading) {
    return null;
  }

  return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
};

/**
 * Hook to access feature flags
 *
 * Usage:
 *   const { physicalScannerEnabled, emailImportEnabled } = useFeatureFlags();
 *
 *   {physicalScannerEnabled && <ScanButton />}
 */
export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
};

export default FeatureFlagContext;
